import { useMemo, useEffect, useState, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { cloneDeep, isEqual, isEmpty } from 'lodash'

import { Box } from '@mui/material'
import {
	TilePaginationDatagrid,
	ControlledTileDatagrid,
} from 'components/pages/dis-v2/_components/TileDatagrid'
import { useGridApiRef } from '@mui/x-data-grid-premium'

import { off, onValue, ref } from 'firebase/database'
import database from '../../../../../../../firebase-config'

import TileWrapper from 'components/pages/dis/components/TileWrapper'

import { useJobSummaryViewReducer } from '../../../job-summary/helper/reducers/jobViewReducer'
import { useRecordQuery } from '../../../utils/tileRecordQuery'

import { getSelectedGridRecord, parsedDataGridColumns } from '../../../../../../data/dataHelpers'
import { getFilteredRows } from '../../../../../../data/dataHelpers'
import { updateColumns } from '../../../../../../data/dataServices'

import { COLUMNS_WITH_DEFAULT_PRESET_VALUE } from '../../../job-summary/helper/constant'
import { TASK_MAP_SETTING_TYPES } from '../../../summaries/utils/constant'
import { sleep } from 'utils/functions/helpers'
import { PlottingMapSettingDialog } from '../../PlottingMapSettingDialog'
import { TYPES_OF_TILE } from '../../../../../../../constants'

export function SummaryMapDatarid(props) {
	const {
		tile,
		settings,
		handleSubmitSetting,
		selectedRow,
		setSelectedRow,
		displayGridRows,
		setDisplayGridRows,
		setDateFilterColumn,
		useTileWrapper = true,
		tileWrapperHeight,
		gridFilterModel,
		onGridFilterModelChange,
		gridRowGroupingModel,
		onGridRowGroupingModelChange,
		gridSortModel,
		onGridSortModelChange,
		gridAggregationModel,
		onGridAggregationModelChange,
		gridColumnVisibilityModel,
		onGridColumnVisibilityModelChange,
		columnsWidth,
		onGridColumnResize,
		columnsOrder,
		onGridColumnOrderChange,
		hasSelectColumns = false,
	} = props
	const { environment } = useSelector((state) => state)

	const {
		tileWidth,
		selectedTaskMapForm,
		taskMapDatePreset,
		columnsChecked,
		summaryAdditionalFilters,
		summaryPrimaryFilters,
	} = useMemo(() => settings, [settings])

	const [settingsOpen, setSettingsOpen] = useState(false)
	const handleOpenDialog = () => {
		setSettingsOpen(true)
	}

	const handleCloseDialog = () => {
		setSettingsOpen(false)
	}

	const gridTileRef = useRef(null)
	const apiRef = useGridApiRef()
	const [gridColumns, setGridColumns] = useState([])
	const [currentRecords, setCurrentRecords] = useState([])

	// selectedjob grid section
	const { viewData, allColumns, serverFilter, viewLoading } = useJobSummaryViewReducer({
		formKey: selectedTaskMapForm?.formKey,
		projectKey: selectedTaskMapForm?.projectKey,
		columnsChecked: [],
		tile,
	})

	const [isLoading, setIsLoading] = useState(false)

	// for Job view
	useEffect(() => {
		if (viewLoading || !viewData || isEmpty(allColumns)) return

		// set the grid columns based on the view data
		async function setViewColumnsFromTileSettings() {
			await updateColumns({
				viewKey: viewData?.key,
				columnsArr: allColumns,
				viewSession: true,
				token: environment?.apiToken,
			})

			setIsLoading(false)
		}
		try {
			setIsLoading(true)
			setViewColumnsFromTileSettings()
		} catch (error) {
			console.error('Error setting view columns:', error)
		}
	}, [viewData, allColumns, viewLoading])

	const selectedTaskMapFormQueryCondition = useMemo(() => {
		if (viewLoading || !viewData) return []

		const currentConditions = cloneDeep(
			!isEmpty(summaryPrimaryFilters)
				? summaryPrimaryFilters
				: viewData?.queries?.[0]?.filter?.conditions
		)
		const updatedConditions = currentConditions.map((condition) => {
			if (!['@StartTime'].includes(condition.target)) {
				if (!condition.preset) return condition

				delete condition['values']
				return condition
			}

			if (taskMapDatePreset && condition.target === '@StartTime') {
				delete condition['values']
				condition.preset = taskMapDatePreset
				return condition
			}

			return condition
		})

		return updatedConditions
	}, [viewData, viewLoading, taskMapDatePreset, summaryPrimaryFilters])

	const { records, recordsLoading, refetchRecords } = useRecordQuery({
		viewKey: viewData ? viewData.key : null,
		viewType: viewData ? viewData.type : null,
		clientFilter:
			{ conditions: selectedTaskMapFormQueryCondition.filter((item) => !isEmpty(item)) } ?? {},
		viewSession: true,
		isUpdateColumnsSession: isLoading,
		prefix: 'task-map-datagrid',
	})

	useEffect(() => {
		const parsedGridColumns = parsedDataGridColumns(environment, allColumns)

		if (isEqual(gridColumns, parsedGridColumns)) return

		setGridColumns(parsedGridColumns)
	}, [allColumns])

	const formattedColumnChecked = useMemo(() => {
		if (
			[TYPES_OF_TILE.TASK_SUMMARY_TILE, TYPES_OF_TILE.JOB_SUMMARY_TILE].includes(tile.type) &&
			columnsChecked?.length === 0
		) {
			return gridColumns.map((col) => col.field || col.name)
		}

		return columnsChecked
	}, [gridColumns, columnsChecked])

	const displayGridColumns = useMemo(() => {
		if (!hasSelectColumns) return gridColumns

		return gridColumns.filter((col) => formattedColumnChecked?.includes(col.field || col.name))
	}, [hasSelectColumns, gridColumns, formattedColumnChecked])

	useEffect(() => {
		if (recordsLoading) return
		if (isEqual(records, currentRecords)) return
		setCurrentRecords(records)
	}, [recordsLoading, records, currentRecords])

	const currentRows = useMemo(() => {
		if (!currentRecords?.length || !allColumns?.length) return []

		const allRows = getSelectedGridRecord({
			records: currentRecords,
			columns: allColumns,
			environment,
			columnCheckList: allColumns,
			allColumns: allColumns,
		})

		const allFilters = [
			...(summaryAdditionalFilters || []),
			...serverFilter,
			...(gridFilterModel?.items || []),
		]
		const filteredRows = getFilteredRows(allFilters, allRows)

		return filteredRows
	}, [
		currentRecords,
		serverFilter,
		JSON.stringify(allColumns),
		summaryAdditionalFilters,
		gridFilterModel,
	])

	useEffect(() => {
		if (isEqual(displayGridRows, currentRows)) return
		setDisplayGridRows(currentRows)
	}, [currentRows])

	useEffect(() => {
		const customerKey = environment.account.key
		if (!customerKey) return
		const customerCountRef = ref(database, `userSessions/customerKey/${customerKey}`)
		const viewRef = ref(database, `userSessions/viewActivity/customerKey/${customerKey}`)

		const customerUnsubscribe = onValue(customerCountRef, (snapshot) => {
			// const data = snapshot.val()
			// const currentDateTime = moment().add(serverOffset, 'milliseconds').format('MM/DD/YYYY HH:mm:ss')

			// if (data && areDatesEqual(data?.Updated, currentDateTime, 'MM/DD/YYYY HH:mm:ss')) {
			sleep(2000).then(() => {
				refetchRecords()
			})
			// }
		})

		const viewUnsubscribe = onValue(viewRef, (snapshot) => {
			sleep(2000).then(() => {
				refetchRecords()
			})
		})

		return () => {
			off(customerCountRef)
			off(viewRef)

			customerUnsubscribe()
			viewUnsubscribe()
		}
	}, [environment.account.key])

	useEffect(() => {
		const formKey = selectedTaskMapForm?.formKey
		const projectKey = selectedTaskMapForm?.projectKey

		if (formKey && projectKey) {
			const formRef = ref(database, 'formSessions/' + projectKey + '/' + formKey)

			const unsubscribe = onValue(formRef, (snapshot) => {
				// Process data when there is a change
				sleep(2000).then(() => {
					refetchRecords()
				})
			})

			// Returns the off function to turn off event listening when the component unmounts
			return () => {
				off(formRef)
				unsubscribe()
			}
		}
	}, [JSON.stringify(selectedTaskMapForm)])

	const handleGridFilterModelChange = useCallback(
		(model) => {
			if (!onGridFilterModelChange) return

			const formattedFilterItems = (model?.items || []).map((filter) => {
				const newFilter = { ...filter }

				if (newFilter.type) return newFilter

				const currentCol = allColumns.find((col) => col.name === newFilter.field)
				if (!currentCol) return newFilter

				newFilter.type = currentCol.type
				return newFilter
			})

			onGridFilterModelChange({ items: formattedFilterItems })
		},
		[allColumns, onGridFilterModelChange]
	)

	return useTileWrapper ? (
		<TileWrapper
			title={tile.type === TYPES_OF_TILE.TASK_SUMMARY_TILE ? 'Task' : 'Job'}
			onSettingClick={handleOpenDialog}
			ref={gridTileRef}
		>
			<PlottingMapSettingDialog
				tileElementWidth={gridTileRef?.current?.clientWidth}
				defaultTileWidth={tileWidth}
				tile={tile}
				settings={settings}
				// isSubmitting={updateTileMutation.isLoading}
				open={settingsOpen}
				onClose={handleCloseDialog}
				onSubmit={handleSubmitSetting}
				// onResizeTileWidth={handleResizeTileWidth}
				showInModal={true}
			/>
			<Box
				sx={{
					position: 'absolute',
					left: 0,
					right: 0,
					top: 30,
					bottom: 0,
					background: '#fff',
					display: 'flex',

					'& .MuiDataGrid-selectedRowCount': {
						opacity: '0 !important',
					},
				}}
			>
				<ControlledTileDatagrid
					datagridStyle={{
						marginLeft: '8px',
						maxHeight: `${tileWrapperHeight}px`,
					}}
					isLoading={recordsLoading}
					tile={tile}
					rows={currentRows}
					columns={displayGridColumns}
					apiRef={apiRef}
					handleSelectRow={(params, type, rows) => {
						if (selectedRow?.id === params.id) {
							setSelectedRow(null)
							return
						}
						const selectedRowData = rows.filter((row) => row.id === params.id)
						setSelectedRow(selectedRowData?.[0])
					}}
					gridFilterModel={gridFilterModel}
					onGridFilterModelChange={handleGridFilterModelChange}
					gridRowGroupingModel={gridRowGroupingModel}
					onGridRowGroupingModelChange={onGridRowGroupingModelChange}
					gridSortModel={gridSortModel}
					onGridSortModelChange={onGridSortModelChange}
					gridAggregationModel={gridAggregationModel}
					onGridAggregationModelChange={onGridAggregationModelChange}
					gridColumnVisibilityModel={gridColumnVisibilityModel}
					onGridColumnVisibilityModelChange={onGridColumnVisibilityModelChange}
					columnsWidth={columnsWidth}
					onGridColumnResize={onGridColumnResize}
					columnsOrder={columnsOrder}
					onGridColumnOrderChange={onGridColumnOrderChange}
				/>
			</Box>
		</TileWrapper>
	) : (
		<Box
			sx={{
				background: '#fff',
				height: '100%',
				width: '100%',

				'& .MuiDataGrid-selectedRowCount': {
					opacity: '0 !important',
				},
			}}
		>
			<TilePaginationDatagrid
				datagridStyle={{
					marginLeft: '8px',
					maxHeight: `${tileWrapperHeight}px`,
				}}
				isLoading={recordsLoading}
				tile={tile}
				rows={currentRows}
				columns={displayGridColumns}
				apiRef={apiRef}
				handleSelectRow={(params, type, rows) => {
					if (selectedRow?.id === params.id) {
						setSelectedRow(null)
						return
					}
					const selectedRowData = rows.filter((row) => row.id === params.id)
					setSelectedRow(selectedRowData?.[0])
				}}
			/>
		</Box>
	)
}
